import { useState } from "react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { formatEur, formatNumber, formatPct } from "../lib/format";
import { AlertTriangle, ChevronDown, ChevronUp, Info, Calculator, Lock } from "lucide-react";

const BENCHMARK_LABEL = {
    comparable_median: "Comparable median",
    user_assumption: "User assumption",
    area_median_fallback: "Area median (fallback)",
    blocked: "Blocked",
};

const CONFIDENCE_STYLE = {
    high: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    medium: "border-amber-500/40 text-amber-300 bg-amber-500/10",
    low: "border-rose-500/40 text-rose-300 bg-rose-500/10",
    blocked: "border-muted-foreground/40 text-muted-foreground bg-muted/10",
};

const PREMIUM_SOURCE_LABEL = {
    verified_from_comps: "Verified from comps",
    user_assumption: "User assumption",
    ai_estimate_needs_verification: "AI estimate — needs verification",
    not_available: "Not available",
};

const FinancialPanel = ({ financial, comparables }) => {
    if (!financial) return null;
    const bm = financial.benchmark || {};
    const insufficient = comparables?.insufficient;

    return (
        <div className="space-y-4" data-testid="financial-panel">
            {/* Benchmark + confidence */}
            <BenchmarkCard benchmark={bm} financial={financial} comparables={comparables} />

            {/* Warnings */}
            {financial.warnings?.length > 0 && (
                <WarningsCard warnings={financial.warnings} />
            )}

            {/* Insufficient comps notice (kept distinct from generic warnings) */}
            {insufficient && (
                <Card className="p-4 border-amber-500/30 bg-amber-500/5" data-testid="financial-insufficient-comps">
                    <div className="flex items-start gap-3">
                        <Lock className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div className="text-xs">
                            <div className="font-medium text-amber-300">Comparable set insufficient</div>
                            <div className="text-muted-foreground mt-0.5">
                                {comparables.blocked_reason} Scenarios below use the area-wide median as a
                                fallback — they are orientation only and cannot validate a margin of safety.
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Three scenarios */}
            <div className="grid md:grid-cols-3 gap-4">
                <ScenarioCard title="Conservative" scen={financial.conservative} tone="muted" testid="scenario-conservative" />
                <ScenarioCard title="Base case" scen={financial.base} tone="primary" testid="scenario-base" />
                <ScenarioCard title="Upside" scen={financial.upside} tone="accent" testid="scenario-upside" />
            </div>

            {/* Negotiation guidance */}
            <NegotiationCard financial={financial} />

            {/* Calculation audit */}
            <AuditCard financial={financial} />
        </div>
    );
};


const BenchmarkCard = ({ benchmark, financial, comparables }) => {
    const conf = financial.confidence_level || "low";
    return (
        <Card className="p-5" data-testid="financial-benchmark">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Exit benchmark</div>
                    <div className="font-grotesk font-semibold text-base mt-1">
                        {BENCHMARK_LABEL[benchmark.type] || "—"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{benchmark.source_label}</div>
                </div>
                <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${CONFIDENCE_STYLE[conf]}`} data-testid="financial-confidence">
                    {conf} confidence
                </Badge>
            </div>
            <Separator className="my-4" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <StatCell label="Subject €/m²" value={comparables?.subject_price_per_sqm ? `€${formatNumber(comparables.subject_price_per_sqm, 0)}` : "—"} />
                <StatCell label="Median €/m²" value={benchmark.median_price_per_sqm_eur ? `€${formatNumber(benchmark.median_price_per_sqm_eur, 0)}` : "—"} hint="Used as the Base-case resale anchor." />
                <StatCell label="P25 €/m²" value={benchmark.p25_price_per_sqm_eur ? `€${formatNumber(benchmark.p25_price_per_sqm_eur, 0)}` : "—"} hint="Lower quartile of the comp set. Conservative-case anchor." />
                <StatCell label="P75 €/m²" value={benchmark.p75_price_per_sqm_eur ? `€${formatNumber(benchmark.p75_price_per_sqm_eur, 0)}` : "—"} hint="Upper quartile. Upside-case anchor." />
                <StatCell label="Comp count" value={benchmark.comp_count || 0} />
                <StatCell label="Renovated comps" value={benchmark.renovated_comp_count ?? 0} hint="Comps with verified renovation evidence (renovated, new build, modern, premium finish)." />
                <StatCell label="Renovation premium" value={benchmark.proven_renovation_premium_pct ? `+${formatPct(benchmark.proven_renovation_premium_pct)}` : "—"} hint={PREMIUM_SOURCE_LABEL[benchmark.renovation_premium_source]} />
                <StatCell label="Area median €/m²" value={benchmark.area_median_price_per_sqm_eur ? `€${formatNumber(benchmark.area_median_price_per_sqm_eur, 0)}` : "—"} hint="Reference only — not used to compute base resale when comps are sufficient." />
            </div>
        </Card>
    );
};


const WarningsCard = ({ warnings }) => (
    <Card className="p-4 border-amber-500/30 bg-amber-500/5" data-testid="financial-warnings">
        <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs">
                <div className="font-medium text-amber-300 mb-1">Underwriting warnings ({warnings.length})</div>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    {warnings.map((w, i) => <li key={`${w.slice(0, 32)}-${i}`} data-testid={`warning-${i}`}>{w}</li>)}
                </ul>
            </div>
        </div>
    </Card>
);


const NegotiationCard = ({ financial }) => {
    const asking = financial.asking_price_eur || financial.inputs?.asking_price_eur;
    return (
        <Card className="p-5" data-testid="financial-negotiation">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
                <h3 className="font-grotesk font-semibold text-sm">Negotiation guidance</h3>
                {asking && (
                    <Badge variant="outline" className="text-[10px]">Asking €{formatNumber(asking, 0)}</Badge>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <BidCell
                    label="Suggested opening bid"
                    value={formatEur(financial.suggested_opening_bid_eur)}
                    hint="Where to open — ~10% below buy_below."
                    testid="bid-opening"
                />
                <BidCell
                    label="Target buy price"
                    value={formatEur(financial.target_buy_price_eur)}
                    hint="Realistic landing zone after negotiation."
                    testid="bid-target"
                    highlight
                />
                <BidCell
                    label="Maximum bid"
                    value={formatEur(financial.maximum_bid_eur)}
                    hint="Capped at asking — model would tolerate more but never bid above asking."
                    testid="bid-max"
                />
                <BidCell
                    label="Walk away"
                    value={formatEur(financial.walk_away_eur)}
                    hint="Above this you lose money. Capped at asking."
                    testid="bid-walkaway"
                />
            </div>
            {financial.asking_below_max_bid && (
                <div className="mt-4 pt-4 border-t border-border/40 text-xs text-muted-foreground">
                    <Info className="w-3 h-3 inline mr-1 text-amber-400" />
                    The model's uncapped max bid would be <strong>€{formatNumber(financial.maximum_bid_eur + Math.abs(financial.asking_vs_max_bid_gap_eur || 0), 0)}</strong> — €{formatNumber(Math.abs(financial.asking_vs_max_bid_gap_eur || 0), 0)} above asking. Output capped at asking; do not bid above asking unless competitive process requires it.
                </div>
            )}
            {!financial.asking_below_max_bid && financial.buy_below_eur < (asking || 0) && (
                <div className="mt-4 pt-4 border-t border-border/40 text-xs text-muted-foreground">
                    <Info className="w-3 h-3 inline mr-1 text-emerald-400" />
                    Buy-below threshold (€{formatNumber(financial.buy_below_eur, 0)}) is <strong>€{formatNumber((asking || 0) - financial.buy_below_eur, 0)} below asking</strong>. Negotiate aggressively or walk.
                </div>
            )}
        </Card>
    );
};


const ScenarioCard = ({ title, scen, tone = "muted", testid }) => (
    <Card
        className={`p-5 ${
            tone === "primary"
                ? "border-primary/50 bg-primary/[0.03]"
                : tone === "accent"
                ? "border-accent/50 bg-accent/[0.04]"
                : ""
        }`}
        data-testid={testid}
    >
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{title}</div>
        <div className="kpi-value text-3xl mt-2" data-testid={`${testid}-net-profit`}>{formatEur(scen.net_profit_eur)}</div>
        <div className="text-[11px] text-muted-foreground mb-3">Net profit</div>
        <ul className="space-y-1.5 text-xs">
            <Row k="Buy" v={formatEur(scen.buy_price_eur)} />
            <Row k="Renovation" v={formatEur(scen.renovation_eur)} />
            <Row k="Resale" v={formatEur(scen.expected_resale_eur)} sub={scen.resale_price_per_sqm_eur ? `€${formatNumber(scen.resale_price_per_sqm_eur, 0)}/m²` : null} />
            <Row k="Total invest" v={formatEur(scen.total_investment_eur)} />
            <Row k="ROI" v={formatPct(scen.roi_pct)} highlight={scen.roi_pct < 0} />
            <Row k="Annualised return" v={formatPct(scen.annualised_irr_pct)} />
            <Row k="Margin of safety" v={formatPct(scen.margin_of_safety_pct)} highlight={scen.margin_of_safety_pct < 0} />
        </ul>
    </Card>
);


const Row = ({ k, v, sub, highlight }) => (
    <li className="flex justify-between gap-3 items-start">
        <span className="text-muted-foreground">{k}</span>
        <div className="text-right">
            <span className={`font-medium tabular-nums ${highlight ? "text-rose-400" : ""}`}>{v}</span>
            {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
        </div>
    </li>
);


const BidCell = ({ label, value, hint, highlight, testid }) => (
    <div className={`p-4 rounded-md ${highlight ? "bg-primary text-primary-foreground" : "bg-muted/60"}`} data-testid={testid}>
        <div className={`text-[10px] uppercase tracking-[0.18em] ${highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{label}</div>
        <div className="font-grotesk font-semibold text-base mt-1">{value}</div>
        {hint && (
            <div className={`text-[11px] mt-1 ${highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{hint}</div>
        )}
    </div>
);


const StatCell = ({ label, value, hint }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <span>{label}</span>
            {hint && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild><Info className="w-3 h-3 cursor-help opacity-60 hover:opacity-100" /></TooltipTrigger>
                        <TooltipContent side="top"><p className="max-w-[260px] text-xs">{hint}</p></TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
        <div className="text-foreground font-medium mt-0.5">{value}</div>
    </div>
);


const AuditCard = ({ financial }) => {
    const [open, setOpen] = useState(false);
    const scenarios = [
        { key: "conservative", label: "Conservative", scen: financial.conservative },
        { key: "base", label: "Base case", scen: financial.base },
        { key: "upside", label: "Upside", scen: financial.upside },
    ];
    return (
        <Card className="p-0 overflow-hidden" data-testid="financial-audit">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full px-5 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors"
                data-testid="audit-toggle"
            >
                <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-accent" />
                    <span className="font-grotesk font-semibold text-sm">Calculation audit</span>
                    <span className="text-xs text-muted-foreground">— every input and output, fully traceable</span>
                </div>
                {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {open && (
                <div className="border-t border-border/40 overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="border-b border-border/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-4 py-2 text-left">Field</th>
                                {scenarios.map((s) => <th key={s.key} className="px-4 py-2 text-right">{s.label}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {AUDIT_ROWS.map((r) => (
                                <tr key={r.key} className="border-b border-border/20 hover:bg-muted/10">
                                    <td className="px-4 py-1.5 text-muted-foreground" data-testid={`audit-row-${r.key}`}>
                                        <span className="flex items-center gap-1.5">
                                            {r.label}
                                            {r.hint && (
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild><Info className="w-3 h-3 opacity-60" /></TooltipTrigger>
                                                        <TooltipContent side="right"><p className="max-w-[280px] text-xs">{r.hint}</p></TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            )}
                                        </span>
                                    </td>
                                    {scenarios.map((s) => (
                                        <td key={s.key} className="px-4 py-1.5 text-right tabular-nums">
                                            {r.render(s.scen?.audit)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
};


const fmtEur = (v) => (v == null ? "—" : `€${formatNumber(v, 0)}`);
const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);
const fmtNum = (v) => (v == null ? "—" : formatNumber(v, 0));
const fmtRaw = (v) => (v == null ? "—" : String(v));

const AUDIT_ROWS = [
    { key: "buy_price_eur", label: "Buy price", render: (a) => fmtEur(a?.buy_price_eur) },
    { key: "discount_to_asking_pct", label: "Discount to asking", hint: "Buy price as a percentage below asking.", render: (a) => fmtPct(a?.discount_to_asking_pct) },
    { key: "renovation_eur_per_sqm", label: "Renovation €/m²", render: (a) => fmtEur(a?.renovation_eur_per_sqm) },
    { key: "renovation_eur", label: "Renovation total", render: (a) => fmtEur(a?.renovation_eur) },
    { key: "tx_buy_eur", label: "Acquisition costs", hint: "Transfer tax + notary + legal + buy-side agent.", render: (a) => fmtEur(a?.tx_buy_eur) },
    { key: "tx_sell_eur", label: "Selling costs", hint: "Sell-side agent commission.", render: (a) => fmtEur(a?.tx_sell_eur) },
    { key: "holding_cost_eur", label: "Holding cost", hint: "Financing interest during the renovation period.", render: (a) => fmtEur(a?.holding_cost_eur) },
    { key: "contingency_eur", label: "Contingency + styling", render: (a) => fmtEur(a?.contingency_eur) },
    { key: "total_project_cost_eur", label: "Total project cost", hint: "Sum of all costs above.", render: (a) => fmtEur(a?.total_project_cost_eur) },
    { key: "resale_price_per_sqm_eur", label: "Exit €/m²", hint: "Resale anchor anchored on the benchmark median (or P25/P75 for Conservative/Upside).", render: (a) => fmtEur(a?.resale_price_per_sqm_eur) },
    { key: "expected_resale_eur", label: "Exit value", render: (a) => fmtEur(a?.expected_resale_eur) },
    { key: "gross_profit_eur", label: "Gross profit", hint: "Resale – buy price – renovation. Ignores transaction costs.", render: (a) => fmtEur(a?.gross_profit_eur) },
    { key: "net_profit_eur", label: "Net profit", hint: "Resale – total project cost (everything in).", render: (a) => fmtEur(a?.net_profit_eur) },
    { key: "roi_pct", label: "ROI", hint: "Net profit / total project cost.", render: (a) => fmtPct(a?.roi_pct) },
    { key: "annualised_return_pct", label: "Annualised return", hint: "(1 + ROI)^(12 / holding months) − 1.", render: (a) => fmtPct(a?.annualised_return_pct) },
    { key: "margin_of_safety_pct", label: "Margin of safety", hint: "(Resale – break-even) / resale.", render: (a) => fmtPct(a?.margin_of_safety_pct) },
    { key: "benchmark_type", label: "Benchmark", hint: "Which yardstick anchors this scenario.", render: (a) => fmtRaw(a?.benchmark_type) },
    { key: "benchmark_median_ppm", label: "Benchmark median €/m²", render: (a) => fmtEur(a?.benchmark_median_ppm) },
    { key: "benchmark_comp_count", label: "Comp count", render: (a) => fmtNum(a?.benchmark_comp_count) },
    { key: "renovation_premium_source", label: "Renovation premium source", render: (a) => PREMIUM_SOURCE_LABEL[a?.renovation_premium_source] || "—" },
    { key: "renovation_premium_pct", label: "Renovation premium", render: (a) => fmtPct(a?.renovation_premium_pct) },
];


export default FinancialPanel;
